import { Box, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import {
  BookkeeperEmployeesDocument,
  BookkeeperFragment,
  BookkeepersDocument,
  CreateBookkeeperAccessDocument,
  CreateBookkeeperAccessMutation,
  DeleteBookkeeperAccessDocument,
  DeleteBookkeeperAccessMutation,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  customerCompanyId: string
  bookkeeperName: string
  bookkeeperEmployeeAccessId: string
  bookkeeper: BookkeeperFragment | undefined
}

const CompanyBookkeeper = ({
  customerCompanyId,
  bookkeeperName,
  bookkeeperEmployeeAccessId,
  bookkeeper,
}: Props) => {
  const [createBookkeeper, { loading: loadingCreate }] = useMutation(
    CreateBookkeeperAccessDocument,
    {
      extractorFn: (data: CreateBookkeeperAccessMutation) =>
        data.bookkeepersManagementMutations.createBookkeepersmanagementBookkeeperAccess,
    }
  )
  const [deleteBookkeeper, { loading: loadingDelete }] = useMutation(
    DeleteBookkeeperAccessDocument,
    {
      extractorFn: (data: DeleteBookkeeperAccessMutation) =>
        data.bookkeepersManagementMutations.deleteBookkeepersmanagementBookkeeperAccess,
    }
  )
  const loading = loadingCreate || loadingDelete

  if (!bookkeeper && !bookkeeperEmployeeAccessId) return null
  const bookkeeperContext = {
    isBookkeeperView: true,
  }

  const showSyncConfirmation = (message: string) => {
    app.setFlash({
      type: 'correct',
      message: message,
    })
  }

  const showErrors = (errors: Record<string, string>) => {
    app.setFlash({
      type: 'wrong',
      message: errors.message || i18n.t('common.unknown'),
    })
  }

  const createBookkeeperAccess = async () => {
    const response = await createBookkeeper({
      context: bookkeeperContext,
      variables: {
        bookkeeperEmployeeAccessId: bookkeeperEmployeeAccessId,
        companyId: customerCompanyId,
      },
      refetchQueries: [BookkeepersDocument, BookkeeperEmployeesDocument],
    })

    const result = handleResult(response, (errors) => {
      showErrors(errors)
      return
    })

    if (result.success) {
      const message = i18n.t('bookkeepers.companies.toggle_bookkeeper.added', {
        bookkeeper: bookkeeperName,
      })
      showSyncConfirmation(message)
    }
  }

  const deleteBookkeeperAccess = async () => {
    if (!bookkeeper) return

    const response = await deleteBookkeeper({
      context: bookkeeperContext,
      variables: {
        bookkeeperId: bookkeeper?.id,
      },
      refetchQueries: [BookkeepersDocument, BookkeeperEmployeesDocument],
    })

    const result = handleResult(response, (errors) => {
      showErrors(errors)
      return
    })

    if (result.success) {
      const message = i18n.t('bookkeepers.companies.toggle_bookkeeper.removed', {
        bookkeeper: bookkeeperName,
      })
      showSyncConfirmation(message)
    }
  }

  const toggleBookkeeperAccess = async (value: boolean) => {
    if (value) createBookkeeperAccess()
    else deleteBookkeeperAccess()
  }

  const canManage = bookkeeper?.access?.isConfirmed ?? false

  return (
    <Box flexDirection="row" gap="s8" alignItems="center">
      <TogglerCheckbox
        disabled={loading}
        value={canManage}
        onChange={(value) => toggleBookkeeperAccess(value)}
      />
      <Box>
        <Text.Normal weight="normal" size="200">
          {bookkeeperName}
        </Text.Normal>
      </Box>
    </Box>
  )
}

export default CompanyBookkeeper
